// src/pages/auth/Login.jsx
import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Link,
  Divider,
  Grid,
  InputAdornment,
  IconButton,
  alpha,
  Paper,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// Import custom components and hooks
import { TextField, Button } from '../../design-system';
import useAuthStore from '../../stores/authStore';
import useUIStore from '../../stores/uiStore';

// Login page component
const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuthStore();
  const { addNotification } = useUIStore();
  
  // Get redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';
  
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  
  // Clear any auth errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);
  
  // Toggle password visibility
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };
  
  // Validation schema using Yup
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password must be at least 8 characters'),
  });
  
  // Formik setup
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await login(values.email, values.password);
        addNotification({
          message: 'Login successful',
          type: 'success'
        });
        navigate(from, { replace: true });
      } catch (err) {
        addNotification({
          message: err.message || 'Login failed. Please try again.',
          type: 'error'
        });
      }
    },
  });
  
  return (
    <Box sx={{ width: '100%' }}>
      <Typography component="h1" variant="h4" fontFamily="Playfair Display" gutterBottom>
        Welcome Back
      </Typography>
      
      <Typography variant="body2" color="text.secondary" mb={3}>
        Sign in to continue to your account
      </Typography>
      
      {/* Error alert */}
      {error && (
        <Paper 
          elevation={0}
          sx={{
            color: 'error.main',
            backgroundColor: alpha('#f44336', 0.1),
            p: 1.5,
            mb: 2.5,
            borderRadius: 1
          }}
        >
          <Typography variant="body2">{error}</Typography>
        </Paper>
      )}
      
      {/* Login form */}
      <form onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          id="email"
          name="email"
          label="Email Address"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          magical
          margin="normal"
          autoComplete="email"
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          fullWidth
          id="password"
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          magical
          margin="normal"
          autoComplete="current-password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 2 }}>
          <Link
            component={RouterLink}
            to="/forgot-password"
            variant="body2"
            underline="hover"
            sx={{ 
              color: 'text.accent',
              fontWeight: 500,
              transition: 'all 0.3s ease',
              '&:hover': {
                color: 'primary.dark',
                transform: 'translateY(-1px)',
              }
            }}
          >
            Forgot password?
          </Link>
        </Box>
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          
          size="large"
          magical
          loading={isLoading}
          sx={{ mb: 3, py: 1.5 }}
        >
          Sign In
        </Button>
        
        {/* <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>
        
        <Grid container justifyContent="center">
          <Grid item>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{' '}
              <Link
                component={RouterLink}
                to="/register"
                variant="body2"
                underline="hover"
                sx={{ 
                  color: 'text.accent',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'primary.dark',
                    transform: 'translateY(-1px)',
                  }
                }}
              >
                Register
              </Link>
            </Typography>
          </Grid>
        </Grid> */}
      </form>
    </Box>
  );
};

export default Login;